import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Users, Loader2, RefreshCw, Plus, X, TrendingUp, DollarSign, AlertCircle, Building2, Library, Bell, Banknote, Search, Check } from 'lucide-react';
import { useWizardStore, type CompetitorData, type CompanyCountry } from '../../stores/wizardStore';
import { useResearchJobsStore } from '../../stores/researchJobsStore';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import { api, indiaCompanyAPI, ukCompanyAPI } from '../../lib/api';
import { toast } from 'sonner';
import PeerFinancialTable from '../../components/wizard/PeerFinancialTable';
import IndustryBenchmarksPanel from '../../components/case-analytics/IndustryBenchmarksPanel';
import { formatCurrencyValue, regionFromCountry } from '../../components/financial/utils/formatters';

interface Step3Props {
  onNext: () => void;
  onBack: () => void;
}

/** A registry hit, normalised across the three country providers so the
 *  dropdown and the add-handler don't care which one answered. Mirrors the
 *  `UnifiedCompany` shape Step 1 uses for the focal company — same id scheme
 *  (`sec_<cik>` / `ind_<scId>` / `uk_<number>`), so a competitor added here is
 *  resolvable by exactly the same backend lookups. */
interface CompetitorSuggestion {
  country: CompanyCountry;
  companyId: string;
  name: string;
  ticker?: string;
  industry?: string;
  cik?: string;
  scId?: string;
  companyNumber?: string;
  /** Secondary line rendered under the name in the dropdown. */
  subtitle?: string;
}

/** Which registry a competitor search hits, keyed by the focal company's
 *  country. A competitor list is only meaningful inside one market, so we
 *  deliberately reuse the case's country rather than offering a picker. */
const COMPETITOR_REGISTRY_LABEL: Record<CompanyCountry, string> = {
  US: 'SEC EDGAR (US)',
  IN: 'NSE / BSE (India)',
  UK: 'Companies House (UK)',
};

/** One entry of `industryInsights.competitiveLandscape.marketLeaders` as
 *  returned by GET /api/case-discovery/industry-insights/{industryId}.
 *  Serialized straight from the backend's `MarketLeader` model, so every
 *  field the AI-generated competitor cards are built from is present. */
interface MarketLeaderDTO {
  companyId?: string | null;
  companyName?: string | null;
  ticker?: string | null;
  cik?: string | null;
  marketShare?: number | null;
  position?: string | null;
  strengths?: string[] | null;
  weaknesses?: string[] | null;
  strategicFocus?: string[] | null;
  momentum?: string | null;
}

/** Normalise a company name for cross-source matching: lowercase, strip the
 *  corporate suffixes that differ between listings ("Wipro Limited" vs
 *  "Wipro"), collapse punctuation. Mirrors `_is_same_company._norm` in
 *  `src/api/routes/case_discovery.py` so the frontend and backend agree on
 *  when two names denote the same company. */
const normalizeCompanyName = (value: string): string =>
  (value || '')
    .toLowerCase()
    .replace(
      /\b(limited|ltd\.?|inc\.?|corporation|corp\.?|plc|company|co\.?|llc|holdings?|group)\b/g,
      '',
    )
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Find the landscape entry that denotes `name`. Exact normalised match first,
 *  then containment, so "Wipro" matches "Wipro Limited" without letting a
 *  short token match an unrelated longer name by accident. */
const findMarketLeader = (
  leaders: MarketLeaderDTO[],
  name: string,
): MarketLeaderDTO | undefined => {
  const target = normalizeCompanyName(name);
  if (!target) return undefined;

  const named = leaders.filter((l) => !!l.companyName);
  const exact = named.find((l) => normalizeCompanyName(l.companyName!) === target);
  if (exact) return exact;

  return named.find((l) => {
    const candidate = normalizeCompanyName(l.companyName!);
    if (!candidate) return false;
    // Guard against 2-3 char tokens matching half the industry.
    const shorter = candidate.length <= target.length ? candidate : target;
    if (shorter.length < 4) return false;
    return candidate.includes(target) || target.includes(candidate);
  });
};

/** Derive the market-position label from market share. Thresholds copied
 *  verbatim from `market_leader_to_wizard_competitor` in
 *  `src/services/case_wizard_adapter.py` so a manually added competitor is
 *  labelled on exactly the same scale as a generated one. */
const positionFromMarketShare = (share: number | null | undefined): string | undefined => {
  if (share == null) return undefined;
  const ms = Number(share);
  if (!Number.isFinite(ms)) return undefined;
  if (ms >= 20) return 'Market Leader';
  if (ms >= 10) return 'Strong Challenger';
  if (ms >= 3) return 'Niche Player';
  return 'Emerging';
};

/** Port of the backend's `market_leader_to_wizard_competitor`. Given a
 *  landscape entry, produce the same CompetitorData slice the AI-generated
 *  cards carry — position, description, strengths, share and identifiers.
 *  Keeping the two in lockstep is the whole point: a manually added
 *  competitor that exists in the landscape must render identically. */
const competitorFieldsFromLeader = (leader: MarketLeaderDTO): Partial<CompetitorData> => {
  const strengths = (leader.strengths || []).filter(Boolean).map(String);
  const weaknesses = (leader.weaknesses || []).filter(Boolean).map(String);
  const strategicFocus = (leader.strategicFocus || []).filter(Boolean).map(String);

  const descriptionParts: string[] = [];
  if (strategicFocus.length > 0) descriptionParts.push(`Strategic focus: ${strategicFocus.join(', ')}`);
  if (leader.momentum) descriptionParts.push(`Momentum: ${leader.momentum}`);

  const share = leader.marketShare == null ? undefined : Number(leader.marketShare);

  return {
    market_position:
      leader.position || positionFromMarketShare(leader.marketShare) || 'Market Player',
    // Same fallback string the generated cards show when the landscape carries
    // no strategic focus or momentum.
    description: descriptionParts.length > 0 ? descriptionParts.join('. ') : 'Competitor in the industry.',
    ...(strengths.length > 0 ? { competitive_advantages: strengths } : {}),
    ...(weaknesses.length > 0 ? { weaknesses } : {}),
    ...(share != null && Number.isFinite(share) ? { market_share: share } : {}),
    ...(leader.cik ? { cik: String(leader.cik) } : {}),
    ...(leader.ticker ? { ticker: String(leader.ticker) } : {}),
    ...(leader.companyId ? { companyId: String(leader.companyId) } : {}),
  };
};

type SmartCompetitionResponse =
  | {
      status: 'completed';
      count: number;
      competitors: CompetitorData[];
      industryId?: string;
      source: 'existing' | 'generated_comprehensive';
    }
  | {
      status: 'running';
      jobId: string;
      message?: string;
    };

export const Step3CompetitionAnalysis: React.FC<Step3Props> = ({ onNext, onBack }) => {
  const navigate = useNavigate();
  const { companyData, industryData, competitorsData, setCompetitorsData } = useWizardStore();

  const [showManualAdd, setShowManualAdd] = useState(false);
  const [researchSource, setResearchSource] = useState<'existing' | 'generated_comprehensive' | null>(null);
  const [manualCompetitor, setManualCompetitor] = useState({
    name: '',
    description: '',
    market_position: '',
  });

  // ── Registry-backed competitor lookup ──────────────────────────────────
  // The name field used to be free text, which produced competitors that no
  // downstream lookup could resolve (no companyId/cik → no peer financials,
  // no Company Research link). It now searches the same per-country registries
  // Step 1 uses for the focal company. Free text is still accepted as a
  // fallback for private/unlisted peers that no registry knows about.
  const competitorCountry: CompanyCountry = companyData?.country || 'US';
  const [competitorQuery, setCompetitorQuery] = useState('');
  const [debouncedCompetitorQuery, setDebouncedCompetitorQuery] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<CompetitorSuggestion | null>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);

  // 300 ms debounce so a fast typist doesn't fire a request per keystroke.
  // The India and UK backends cache for 5 min, and TanStack Query dedupes,
  // but SEC EDGAR has no cache in front of it.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompetitorQuery(competitorQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [competitorQuery]);

  // Scope the active-competition-job lookup to the current company. See the
  // matching comment in Step2IndustryResearch — without this scope, a job
  // from a previous wizard session would render this step's full-screen
  // progress card for the wrong case.
  const currentCompanyName = companyData?.name;
  const activeCompetitionJob = useResearchJobsStore((s) =>
    Object.values(s.jobs).find(
      (j) =>
        j.kind === 'competition_analysis' &&
        j.status === 'running' &&
        (j.companyName ?? '').trim().toLowerCase() ===
          (currentCompanyName ?? '').trim().toLowerCase(),
    ),
  );
  const registerJob = useResearchJobsStore((s) => s.registerJob);

  // Focal-company financial snapshot — used by PeerFinancialTable to show the
  // focal row alongside peers. Read-only; doesn't mutate the wizard store.
  // We pass `name` so the backend can resolve via name-match when the wizard
  // supplied a synthetic / slugified companyId that was never stored in Neo4j.
  const focalSnapshotQuery = useQuery<any>({
    queryKey: ['case-context', 'financial-snapshot', companyData?.companyId, companyData?.name],
    queryFn: () =>
      api.get(
        `/case-discovery/companies/${encodeURIComponent(companyData?.companyId || companyData?.name || '')}/financial-snapshot`,
        companyData?.name ? { name: companyData.name } : undefined,
      ),
    enabled: !!(companyData?.companyId || companyData?.name),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  // Per-country competitor search. Same endpoints Step 1 uses for the focal
  // company, so the two steps always agree on what a company "is".
  const {
    data: competitorSuggestions = [],
    isFetching: isSearchingCompetitors,
    isError: competitorSearchFailed,
  } = useQuery<CompetitorSuggestion[]>({
    queryKey: ['competitor-search', competitorCountry, debouncedCompetitorQuery],
    queryFn: async () => {
      const q = debouncedCompetitorQuery;
      if (q.length < 2) return [];

      if (competitorCountry === 'IN') {
        // POST /api/finance/search-companies → Yahoo Finance, 5-min server cache.
        const res = await indiaCompanyAPI.searchCompanies(q);
        return (res.results || [])
          .map((r: any) => ({
            country: 'IN' as CompanyCountry,
            companyId: `ind_${r.sc_id || String(r.stock_name || '').toLowerCase().replace(/\s+/g, '_')}`,
            scId: r.sc_id || undefined,
            name: r.stock_name,
            ticker: r.sc_nse_symbol || r.sc_bse_symbol || undefined,
            // Yahoo classifies Indian equities in the search response itself,
            // same as the SEC branch below. Feeds the "Competitor in {sector}"
            // description fallback in handleAddManualCompetitor.
            industry: r.industry || r.sector || undefined,
            subtitle:
              [r.sc_nse_symbol || r.sc_bse_symbol, r.industry || r.sector]
                .filter(Boolean)
                .join(' · ') || undefined,
          }))
          .filter((s) => !!s.name);
      }

      if (competitorCountry === 'UK') {
        // GET /api/finance/uk/search-companies → Companies House, 5-min cache.
        const res = await ukCompanyAPI.searchCompanies(q, 10);
        return (res.results || [])
          .map((r) => ({
            country: 'UK' as CompanyCountry,
            companyId: `uk_${r.company_number}`,
            companyNumber: r.company_number,
            name: r.company_name,
            subtitle: [r.company_number, r.company_status].filter(Boolean).join(' · ') || undefined,
          }))
          .filter((s) => !!s.name);
      }

      // GET /api/companies/search?query= → SEC EDGAR company tickers (limit 20).
      const raw = await api.get<Array<{ cik: string; name: string; ticker?: string; industry?: string }>>(
        '/companies/search',
        { query: q },
      );
      return (raw || [])
        .map((c) => ({
          country: 'US' as CompanyCountry,
          companyId: `sec_${c.cik}`,
          cik: c.cik,
          name: c.name,
          ticker: c.ticker,
          industry: c.industry,
          subtitle: [c.ticker, c.industry].filter(Boolean).join(' · ') || undefined,
        }))
        .filter((s) => !!s.name);
    },
    enabled: showManualAdd && debouncedCompetitorQuery.length >= 2 && !selectedSuggestion,
    staleTime: 60_000,
    retry: false,
  });

  // Industry competitive landscape — the source the AI-generated competitor
  // cards are built from. Step 3 only renders `marketLeaders[:topN]`, so the
  // landscape routinely holds peers the list never showed; matching a manually
  // added competitor against it gives that card full parity (position, share,
  // strengths, description) instead of a bare name.
  //
  // Same queryKey as IndustryInsightsPanel / SuggestedThemeChips so TanStack
  // dedupes rather than re-fetching.
  const { data: industryInsights } = useQuery<any>({
    queryKey: ['case-context', 'industry-insights', industryData?.industryId],
    queryFn: () => api.get(`/case-discovery/industry-insights/${industryData?.industryId}`),
    enabled: !!industryData?.industryId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  // Memoised so the `?? []` fallback doesn't produce a fresh array identity on
  // every render and invalidate the match memo below.
  const marketLeaders: MarketLeaderDTO[] = useMemo(
    () => industryInsights?.industryInsights?.competitiveLandscape?.marketLeaders ?? [],
    [industryInsights],
  );

  // Landscape hit for whatever is currently selected/typed — drives the
  // "full profile available" hint before the user commits to adding.
  const pendingLeaderMatch = useMemo(() => {
    const name = selectedSuggestion?.name || manualCompetitor.name;
    if (!name || marketLeaders.length === 0) return undefined;
    return findMarketLeader(marketLeaders, name);
  }, [selectedSuggestion?.name, manualCompetitor.name, marketLeaders]);

  // Auto-trigger on mount if no competitors yet
  useEffect(() => {
    if (competitorsData.length === 0 && companyData && !activeCompetitionJob) {
      handleGenerateCompetitors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateCompetitors = useMutation({
    mutationFn: async () => {
      if (!companyData || !industryData) {
        throw new Error('Company and industry data required');
      }

      return api.post<SmartCompetitionResponse>('/case-discovery/competition-analysis/smart', {
        companyName: companyData.name,
        industryName: industryData.name,
        industryId: industryData.industryId,
        topN: 5,
        createdBy: 'case_wizard',
      });
    },
    onSuccess: (response) => {
      if (response.status === 'completed') {
        const mapped: CompetitorData[] = response.competitors.map((comp, index) => ({
          ...comp,
          competitorId: (comp as any).competitorId ?? `comp_${Date.now()}_${index}`,
          selected: comp.selected ?? index < 3,
        }));
        setCompetitorsData(mapped);
        setResearchSource(response.source);
        toast.success(
          response.source === 'existing'
            ? `${response.count} competitors loaded from competitive landscape`
            : `${response.count} competitors generated`,
        );
      } else {
        registerJob({
          jobId: response.jobId,
          kind: 'competition_analysis',
          label: `Competition analysis for ${companyData?.name ?? 'company'}`,
          companyName: companyData?.name,
          industryName: industryData?.name,
          resumePath: '/cases/new-wizard',
        });
        toast.info(response.message ?? 'Generating competitive landscape in the background.', {
          duration: 6000,
        });
      }
    },
    onError: (error: any) => {
      console.error('Competitor identification failed:', error);
      toast.error(`Competition analysis failed: ${error.message || 'Unknown error'}`);
    },
  });

  const handleGenerateCompetitors = () => {
    generateCompetitors.mutate();
  };

  const isGenerating = generateCompetitors.isPending || !!activeCompetitionJob;

  const handleToggleCompetitor = (competitorId: string) => {
    const updated = competitorsData.map((c) =>
      c.competitorId === competitorId ? { ...c, selected: !c.selected } : c,
    );
    setCompetitorsData(updated);
  };

  const resetManualAdd = () => {
    setManualCompetitor({ name: '', description: '', market_position: '' });
    setCompetitorQuery('');
    setDebouncedCompetitorQuery('');
    setSelectedSuggestion(null);
    setSuggestionsOpen(false);
    setShowManualAdd(false);
  };

  const handleSelectSuggestion = (suggestion: CompetitorSuggestion) => {
    setSelectedSuggestion(suggestion);
    setCompetitorQuery(suggestion.name);
    setSuggestionsOpen(false);
    setManualCompetitor((prev) => ({ ...prev, name: suggestion.name }));
  };

  const handleClearSuggestion = () => {
    setSelectedSuggestion(null);
    setCompetitorQuery('');
    setDebouncedCompetitorQuery('');
    setManualCompetitor((prev) => ({ ...prev, name: '' }));
    setSuggestionsOpen(true);
  };

  /** Add the competitor. When it came from the registry we also pull its
   *  latest financials so the card matches an AI-generated one — the snapshot
   *  endpoint resolves by companyId → CIK → name and returns nulls (200, not
   *  404) when the company was never researched, so this is safe to always try
   *  and never blocks the add. */
  const handleAddManualCompetitor = async () => {
    const suggestion = selectedSuggestion;
    const name = (suggestion?.name || manualCompetitor.name || competitorQuery).trim();
    if (!name || isAddingCompetitor) return;

    const focalName = (companyData?.name || '').trim().toLowerCase();
    if (focalName && name.toLowerCase() === focalName) {
      toast.error(`${name} is this case's focal company — pick a different competitor.`);
      return;
    }
    if (competitorsData.some((c) => (c.name || '').trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`${name} is already in the competitor list.`);
      return;
    }

    // Tier 1 — landscape parity. When the competitor is one of the industry's
    // market leaders, adopt the exact same field mapping the generated cards
    // use, so the two are indistinguishable. Anything the user typed by hand
    // still wins: an explicit entry is a deliberate override, not a default.
    const leader = findMarketLeader(marketLeaders, name);
    const leaderFields = leader ? competitorFieldsFromLeader(leader) : {};

    let competitor: CompetitorData = {
      competitorId: suggestion?.companyId || leader?.companyId || `manual_comp_${Date.now()}`,
      name,
      selected: true,
      ...leaderFields,
      // Registry identifiers override the landscape's, which are often absent
      // or stale (the landscape is LLM-generated, the registry is canonical).
      ...(suggestion
        ? {
            companyId: suggestion.companyId,
            country: suggestion.country,
            ticker: suggestion.ticker ?? leaderFields.ticker,
            cik: suggestion.cik ?? leaderFields.cik,
            scId: suggestion.scId,
            companyNumber: suggestion.companyNumber,
          }
        : {}),
      // User-typed values take precedence over everything above.
      ...(manualCompetitor.description ? { description: manualCompetitor.description } : {}),
      ...(manualCompetitor.market_position
        ? { market_position: manualCompetitor.market_position }
        : {}),
    };

    if (!competitor.market_position) competitor.market_position = 'Unknown';

    if (suggestion) {
      setIsAddingCompetitor(true);
      try {
        const snapshot = await api.get<any>(
          `/case-discovery/companies/${encodeURIComponent(suggestion.companyId)}/financial-snapshot`,
          { name: suggestion.name },
        );

        const f = snapshot?.financials;
        if (f) {
          const financials: NonNullable<CompetitorData['financials']> = {};
          if (f.revenue != null) financials.revenue = f.revenue;
          if (f.grossMargin != null) financials.gross_margin = f.grossMargin;
          if (f.operatingMargin != null) financials.operating_margin = f.operatingMargin;
          if (f.period) financials.fiscal_period = f.period;
          if (Object.keys(financials).length > 0) competitor = { ...competitor, financials };
        }

        // Prefer the sector the graph actually holds over the registry's label.
        const sector = snapshot?.company?.sector || suggestion.industry;
        if (!competitor.description && sector) {
          competitor = { ...competitor, description: `Competitor in ${sector}.` };
        }
      } catch (error) {
        // Non-fatal by design: an unresearched competitor is still a valid
        // competitor, it just shows no financials until someone researches it.
        console.warn('[competitor-add] financial enrichment failed (non-fatal):', error);
      } finally {
        setIsAddingCompetitor(false);
      }
    }

    setCompetitorsData([...competitorsData, competitor]);
    resetManualAdd();

    const filled = [
      leader ? 'competitive landscape' : null,
      competitor.financials ? 'Company Research financials' : null,
    ].filter(Boolean);

    if (filled.length > 0) {
      toast.success(`${name} added`, { description: `Profile filled from ${filled.join(' + ')}.` });
    } else if (suggestion) {
      toast.success(`${name} added`, {
        description: 'No profile data yet — research this company to populate its card.',
      });
    } else {
      toast.success(`${name} added manually`);
    }
  };

  const handleRemoveCompetitor = (competitorId: string) => {
    setCompetitorsData(competitorsData.filter((c) => c.competitorId !== competitorId));
  };

  const selectedCount = competitorsData.filter((c) => c.selected).length;
  const canProceed = () => selectedCount >= 2;

  // Loader screen — covers BOTH states with no competitors yet:
  //   1. activeCompetitionJob != null → backend kicked off a background job, we
  //      have a job id + live progress %.  Used when the response was async.
  //   2. generateCompetitors.isPending → mutation is in-flight waiting for the
  //      synchronous response (e.g. cache hit returning competitors directly).
  //      Previously this case rendered the empty main layout with no feedback,
  //      so the user saw a frozen "Competition Analysis" header for 5-30s.
  if ((activeCompetitionJob || generateCompetitors.isPending) && competitorsData.length === 0) {
    const isBackgroundJob = !!activeCompetitionJob;
    const progress = activeCompetitionJob?.progress ?? null;
    const jobMessage = activeCompetitionJob?.message;

    return (
      <div className="max-w-4xl mx-auto">
        <Card className="p-12">
          <div className="text-center space-y-6">
            <Loader2 className="w-16 h-16 mx-auto text-primary-600 animate-spin" />
            <div>
              <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                {isBackgroundJob
                  ? 'Building competitive landscape…'
                  : 'Identifying top competitors…'}
              </h3>
              <p className="text-neutral-600">
                {jobMessage ||
                  `Analyzing competitive dynamics for ${companyData?.name} in ${industryData?.name}.`}
              </p>
              <p className="text-sm text-neutral-500 mt-3">
                {isBackgroundJob
                  ? "Typical run time: 30–60 seconds. Feel free to switch to another tab — we'll notify you when it's ready."
                  : 'Checking the competitive landscape library and falling back to AI generation if needed.'}
              </p>
            </div>

            {/* Progress bar — determinate when a background job exists, otherwise an indeterminate pulse */}
            <div className="w-full max-w-md mx-auto">
              <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                {progress !== null ? (
                  <div
                    className="h-full bg-primary-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                ) : (
                  <div className="h-full w-1/3 bg-primary-500 rounded-full animate-pulse" />
                )}
              </div>
              {progress !== null && (
                <p className="text-xs text-neutral-500 mt-1">{progress}%</p>
              )}
            </div>

            {isBackgroundJob && (
              <div className="flex items-center justify-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 max-w-md mx-auto">
                <Bell className="w-4 h-4" />
                <span className="text-sm">You'll get a notification when the landscape is ready.</span>
              </div>
            )}
          </div>
        </Card>

        <div className="flex justify-between pt-6 border-t border-neutral-200 mt-6">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          {isBackgroundJob && (
            <Button variant="outline" onClick={() => navigate('/case-library')}>
              Leave wizard
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-neutral-900">Competition Analysis</h2>
        <p className="text-neutral-600">Competitors for {companyData?.name}</p>
      </div>

      {/* Source banner */}
      {researchSource === 'existing' && (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Library className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-900">
              Pulled from competitive landscape in the Industry Research library
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Click "Regenerate" to refresh the landscape.
            </p>
          </div>
        </div>
      )}

      <Card className="p-4 bg-gradient-to-r from-primary-50 to-white border-primary-200">
        <div className="flex items-center gap-4">
          <Building2 className="w-8 h-8 text-primary-600" />
          <div className="flex-1">
            <p className="text-sm text-neutral-600">Target Company</p>
            <p className="font-semibold text-neutral-900">{companyData?.name}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm text-neutral-600">Industry</p>
            <p className="font-semibold text-neutral-900">{industryData?.name}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm text-neutral-600">Market Size</p>
            <p className="font-semibold text-neutral-900">
              {(() => {
                // Country-aware Market Size header. When the adapter resolved
                // a country-specific value (industryData.marketScope === "country"),
                // render with the selected country's currency symbol + unit.
                // Otherwise show the global TAM in USD.
                const scope = (industryData as any)?.marketScope;
                const region = scope === 'country' ? regionFromCountry(companyData?.country) : undefined;
                const size = industryData?.marketSizeUSD ?? 0;
                return region
                  ? formatCurrencyValue(size, region, /* compact */ true)
                  : `$${(size / 1e12).toFixed(2)}T`;
              })()}
            </p>
          </div>
        </div>
      </Card>

      {/* Peer-median benchmarks (EBITDA margin / ROIC vs WACC / EV/EBITDA) for
          at-a-glance industry context while the user reviews competitors. Pulls
          from /v2/financial/industry/{id} (same endpoint that powers the
          Industry Research → Financial tab). No-ops when industryId is missing
          or no market leaders are linked to Company Research yet. */}
      {industryData?.industryId && (
        <IndustryBenchmarksPanel industryId={industryData.industryId} mode="summary" />
      )}

      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleGenerateCompetitors} disabled={isGenerating} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </Button>
          <Button
            variant="outline"
            onClick={() => (showManualAdd ? resetManualAdd() : setShowManualAdd(true))}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Manually
          </Button>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Users className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-neutral-700">
            {selectedCount} of {competitorsData.length} selected
          </span>
        </div>
      </div>

      {showManualAdd && (
        <Card className="p-6 bg-neutral-50">
          <div className="flex items-start justify-between gap-4 mb-1">
            <h3 className="text-lg font-semibold text-neutral-900">Add Competitor</h3>
            <span className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-neutral-200 text-xs text-neutral-600">
              <Library className="w-3.5 h-3.5" />
              {COMPETITOR_REGISTRY_LABEL[competitorCountry]}
            </span>
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            Search the registry so the competitor resolves to a real company record — financials are
            pulled in automatically when it already exists in Company Research.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Name → registry typeahead */}
            <div className="relative">
              {selectedSuggestion ? (
                <div className="flex items-center gap-2 w-full px-3 py-2 bg-white border border-emerald-300 rounded-lg">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {selectedSuggestion.name}
                    </p>
                    {selectedSuggestion.subtitle && (
                      <p className="text-xs text-neutral-500 truncate">{selectedSuggestion.subtitle}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleClearSuggestion}
                    className="text-neutral-400 hover:text-neutral-700 transition-colors shrink-0"
                    title="Clear selection"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Input
                  type="text"
                  placeholder="Search competitor name *"
                  leftIcon={<Search className="w-4 h-4" />}
                  rightIcon={
                    isSearchingCompetitors ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined
                  }
                  value={competitorQuery}
                  autoFocus
                  onFocus={() => setSuggestionsOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setSuggestionsOpen(false);
                  }}
                  onChange={(e) => {
                    setCompetitorQuery(e.target.value);
                    setManualCompetitor({ ...manualCompetitor, name: e.target.value });
                    setSuggestionsOpen(true);
                  }}
                />
              )}

              {/* Results dropdown. The fixed transparent layer behind it closes
                  the list on any outside click without blur/focus races. */}
              {suggestionsOpen && !selectedSuggestion && debouncedCompetitorQuery.length >= 2 && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSuggestionsOpen(false)} />
                  <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-neutral-200 rounded-lg shadow-lg">
                    {isSearchingCompetitors && competitorSuggestions.length === 0 ? (
                      <div className="flex items-center gap-2 px-3 py-3 text-sm text-neutral-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching {COMPETITOR_REGISTRY_LABEL[competitorCountry]}…
                      </div>
                    ) : competitorSuggestions.length > 0 ? (
                      competitorSuggestions.map((suggestion, idx) => (
                        <button
                          // India results can share a slug-derived id when the
                          // upstream row has no sc_id — index keeps keys unique.
                          key={`${suggestion.companyId}-${idx}`}
                          type="button"
                          onClick={() => handleSelectSuggestion(suggestion)}
                          className="w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors border-b border-neutral-100 last:border-b-0"
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-neutral-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-neutral-900 truncate">
                                {suggestion.name}
                              </p>
                              {suggestion.subtitle && (
                                <p className="text-xs text-neutral-500 truncate">{suggestion.subtitle}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-sm text-neutral-500">
                        {competitorSearchFailed
                          ? 'Registry search is unavailable right now.'
                          : `No match in ${COMPETITOR_REGISTRY_LABEL[competitorCountry]}.`}{' '}
                        <span className="text-neutral-600">
                          You can still add “{debouncedCompetitorQuery}” as a manual entry.
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <Input
              type="text"
              placeholder="Market Position"
              value={manualCompetitor.market_position}
              onChange={(e) => setManualCompetitor({ ...manualCompetitor, market_position: e.target.value })}
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleAddManualCompetitor}
                disabled={!(selectedSuggestion || manualCompetitor.name.trim()) || isAddingCompetitor}
                className="flex items-center gap-2"
              >
                {isAddingCompetitor && <Loader2 className="w-4 h-4 animate-spin" />}
                {isAddingCompetitor ? 'Adding…' : 'Add'}
              </Button>
              <Button variant="outline" onClick={resetManualAdd} disabled={isAddingCompetitor}>
                Cancel
              </Button>
            </div>
          </div>
          <Input
            type="text"
            placeholder="Description (optional)"
            value={manualCompetitor.description}
            onChange={(e) => setManualCompetitor({ ...manualCompetitor, description: e.target.value })}
          />

          {/* Landscape parity hint. Tells the user, before they commit, that
              this competitor will arrive with the same profile depth as the
              generated cards — and which fields they'd be overriding by
              filling the two inputs above by hand. */}
          {pendingLeaderMatch && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-900">
                <span className="font-medium">
                  Found in this industry&apos;s competitive landscape
                </span>
                <span className="text-emerald-800">
                  {' '}
                  — position, market share and key strengths will be filled automatically
                  {(pendingLeaderMatch.position ||
                    positionFromMarketShare(pendingLeaderMatch.marketShare)) &&
                    ` (${
                      pendingLeaderMatch.position ||
                      positionFromMarketShare(pendingLeaderMatch.marketShare)
                    }`}
                  {pendingLeaderMatch.marketShare != null &&
                    `, ${Number(pendingLeaderMatch.marketShare)}% share`}
                  {(pendingLeaderMatch.position ||
                    positionFromMarketShare(pendingLeaderMatch.marketShare)) &&
                    ')'}
                  . Anything you type above overrides it.
                </span>
              </div>
            </div>
          )}
        </Card>
      )}

      {competitorsData.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {competitorsData.map((competitor) => (
            <Card
              key={competitor.competitorId}
              className={`p-5 transition-all ${
                competitor.selected ? 'border-2 border-primary-500 bg-primary-50' : 'border border-neutral-200 hover:shadow-md'
              }`}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={competitor.selected}
                        onChange={() => handleToggleCompetitor(competitor.competitorId!)}
                        className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-neutral-900">{competitor.name}</h3>
                        <span
                          className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                            competitor.market_position === 'Market Leader'
                              ? 'bg-green-100 text-green-700'
                              : competitor.market_position === 'Strong Challenger'
                              ? 'bg-blue-100 text-blue-700'
                              : competitor.market_position === 'Niche Player'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-neutral-100 text-neutral-700'
                          }`}
                        >
                          {competitor.market_position}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveCompetitor(competitor.competitorId!)}
                    className="text-neutral-400 hover:text-red-600 transition-colors"
                    title="Remove competitor"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {competitor.description && (
                  <p className="text-sm text-neutral-600 leading-relaxed">{competitor.description}</p>
                )}

                {competitor.financials && (
                  <div className="grid grid-cols-2 gap-3">
                    {competitor.financials.revenue !== undefined && competitor.financials.revenue !== null && (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        {/* Country-neutral Banknote icon — the formatted value
                            already carries the correct currency symbol (₹/$/£)
                            so showing a $ icon for India/UK was misleading. */}
                        <Banknote className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="text-xs text-neutral-600">Revenue</p>
                          <p className="font-semibold text-sm text-neutral-900">
                            {(() => {
                              const region = regionFromCountry(companyData?.country);
                              return region
                                ? formatCurrencyValue(competitor.financials!.revenue, region, /* compact */ true)
                                : `${(competitor.financials!.revenue! / 1e9).toFixed(2)}B`;
                            })()}
                          </p>
                        </div>
                      </div>
                    )}
                    {competitor.financials.gross_margin !== undefined && competitor.financials.gross_margin !== null && (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <div>
                          <p className="text-xs text-neutral-600">Gross Margin</p>
                          <p className="font-semibold text-sm text-neutral-900">
                            {(Math.round((competitor.financials.gross_margin ?? 0) * 100) / 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    )}
                    {competitor.financials.operating_margin !== undefined && competitor.financials.operating_margin !== null && (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        <TrendingUp className="w-4 h-4 text-amber-600" />
                        <div>
                          <p className="text-xs text-neutral-600">Operating Margin</p>
                          <p className="font-semibold text-sm text-neutral-900">
                            {(Math.round((competitor.financials.operating_margin ?? 0) * 100) / 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    )}
                    {competitor.financials.revenue_growth !== undefined && (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-xs text-neutral-600">Growth</p>
                          <p className="font-semibold text-sm text-neutral-900">
                            {competitor.financials.revenue_growth?.toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {competitor.market_share != null && (
                  <div>
                    <div className="flex justify-between text-xs text-neutral-600 mb-1">
                      <span>Market Share</span>
                      <span className="font-semibold">{competitor.market_share}%</span>
                    </div>
                    <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 transition-all"
                        style={{ width: `${Math.min(100, competitor.market_share)}%` }}
                      />
                    </div>
                  </div>
                )}

                {competitor.competitive_advantages && competitor.competitive_advantages.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-neutral-600 mb-2">Key Strengths:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {competitor.competitive_advantages.slice(0, 3).map((advantage, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-white rounded-full text-xs text-neutral-700 border border-neutral-200"
                        >
                          {advantage}
                        </span>
                      ))}
                      {competitor.competitive_advantages.length > 3 && (
                        <span className="px-2 py-0.5 text-xs text-neutral-500">
                          +{competitor.competitive_advantages.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
          <h3 className="text-xl font-semibold text-neutral-700 mb-2">No competitors identified</h3>
          <p className="text-neutral-500 mb-6">
            Generate AI-powered competitor analysis or add competitors manually
          </p>
          <Button onClick={handleGenerateCompetitors} variant="primary">
            Generate Competitors
          </Button>
        </Card>
      )}

      {/* Peer financial comparison table — read-only enrichment.
          Uses existing competitor.financials field (populated by backend's
          market_leader_to_wizard_competitor when leader is linked to Company
          Research). Empty rows show "Add to Company Research" CTA. */}
      {competitorsData.length > 0 && (
        <PeerFinancialTable
          competitors={competitorsData}
          focalFinancials={
            focalSnapshotQuery.data?.financials
              ? {
                  revenue: focalSnapshotQuery.data.financials.revenue,
                  grossMargin: focalSnapshotQuery.data.financials.grossMargin,
                  operatingMargin: focalSnapshotQuery.data.financials.operatingMargin,
                  period: focalSnapshotQuery.data.financials.period,
                }
              : null
          }
          focalName={companyData?.name}
          industryId={industryData?.industryId}
          country={companyData?.country}
        />
      )}

      {!canProceed() && competitorsData.length > 0 && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            Please select at least 2 competitors to proceed with the analysis
          </p>
        </div>
      )}

      <div className="flex justify-between pt-6 border-t border-neutral-200">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button variant="primary" onClick={onNext} disabled={!canProceed()} className="flex items-center gap-2">
          Next: Strategic Questions
        </Button>
      </div>
    </div>
  );
};

export default Step3CompetitionAnalysis;
